import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  FlatList,
  TextInput,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';

import { X, Search } from './../icons';
import { useAuth } from '../../contexts/AuthContext';
import { teamMembersService } from '../../services/teamMembersService';
import { spacing } from '../../theme/spacing';
import {
  TeamMember,
  PartnerContributorWithAccess,
  PartnerAccessLevel,
} from '../../types';
import {
  extractViewerDomain,
  isInternalPartnerViewer,
  isPublicViewerDomain,
} from '../../utils/partnerViewerScope';
import { KeyboardDismissToolbar } from '../shared/KeyboardDismissToolbar';

const PARTNER_CONTRIBUTOR_ACCESSORY_VIEW_ID =
  'partner-contributor-keyboard-dismiss';

interface PartnerContributorSelectorProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (selectedPartners: PartnerContributorWithAccess[]) => void;
  selectedIds: PartnerContributorWithAccess[]; // Array of partner contributors with access levels
  title: string;
  /**
   * The request's requestor. They already have full access, and a partner row for them
   * blocks their own Submit, so they are not offered here. The server rejects it too.
   */
  requestorEmail?: string;
}

export const PartnerContributorSelector: React.FC<
  PartnerContributorSelectorProps
> = ({
  visible,
  onClose,
  onSelect,
  selectedIds = [],
  title,
  requestorEmail,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<TeamMember[]>([]);
  const [selectedPartners, setSelectedPartners] =
    useState<PartnerContributorWithAccess[]>(selectedIds);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const viewerRoles = user?.roles ?? [];
  const isInternalViewer = isInternalPartnerViewer(viewerRoles);
  const viewerDomain = extractViewerDomain(user?.email);
  const isPersonalEmailViewer =
    !isInternalViewer &&
    Boolean(viewerDomain) &&
    isPublicViewerDomain(viewerDomain ?? '');
  const isMalformedEmailViewer = !isInternalViewer && !viewerDomain;
  const hasEmptyScope = isPersonalEmailViewer || isMalformedEmailViewer;
  const showScopeBanner =
    !isInternalViewer && Boolean(viewerDomain) && !isPersonalEmailViewer;

  // Load team members when modal opens
  useEffect(() => {
    const loadTeamMembers = async () => {
      setLoading(true);
      try {
        const data = hasEmptyScope
          ? []
          : await teamMembersService.getUsersByRole('partner_contributor');
        const requestor = requestorEmail?.trim().toLowerCase();
        const scoped = (
          isInternalViewer
            ? data
            : data.filter(
                member => extractViewerDomain(member.email) === viewerDomain,
              )
        ).filter(
          member => !requestor || member.email.toLowerCase() !== requestor,
        );
        const listedEmails = new Set(
          scoped.map(member => member.email.toLowerCase()),
        );
        const legacySelected = selectedIds
          .filter(partner => !listedEmails.has(partner.email.toLowerCase()))
          .map(
            (partner): TeamMember => ({
              id: `selected-${partner.email}`,
              name: partner.name || partner.email,
              email: partner.email,
              type: 'partner_contributor',
            }),
          );
        const merged = [...legacySelected, ...scoped].sort((a, b) => {
          const nameA = (a.name || a.email || '').toLowerCase();
          const nameB = (b.name || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setMembers(merged);
        setFilteredMembers(merged);
      } catch (error) {
        console.error('Failed to load partner contributors:', error);
      } finally {
        setLoading(false);
      }
    };

    if (visible) {
      loadTeamMembers();
      setSelectedPartners(selectedIds);
    }
  }, [
    visible,
    selectedIds,
    hasEmptyScope,
    isInternalViewer,
    viewerDomain,
    requestorEmail,
  ]);

  // Sort with selected items at top, then alphabetically within each group
  const sortWithSelectedFirst = (
    items: TeamMember[],
    selectedEmails: string[],
  ) => {
    return [...items].sort((a, b) => {
      const aSelected = selectedEmails.includes(a.email);
      const bSelected = selectedEmails.includes(b.email);

      // If selection status differs, selected items come first
      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1;
      }

      // Within the same selection status, sort alphabetically
      const nameA = (a.name || a.email || '').toLowerCase();
      const nameB = (b.name || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Filter and sort members when search query or selection changes
  useEffect(() => {
    const filterMembers = () => {
      let filtered = members;

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = members.filter(
          member =>
            (member.email || '').toLowerCase().includes(query) ||
            (member.firstName || '').toLowerCase().includes(query) ||
            (member.lastName || '').toLowerCase().includes(query) ||
            (member.name || '').toLowerCase().includes(query),
        );
      }

      // Sort with selected items at top, then alphabetically
      const selectedEmails = selectedPartners.map(p => p.email);
      const sorted = sortWithSelectedFirst(filtered, selectedEmails);
      setFilteredMembers(sorted);
    };

    filterMembers();
  }, [searchQuery, members, selectedPartners]);

  const toggleSelection = (email: string) => {
    setSelectedPartners(prev => {
      const exists = prev.find(p => p.email === email);
      if (exists) {
        return prev.filter(p => p.email !== email);
      }
      if (hasEmptyScope) {
        return prev;
      }
      const member = members.find(m => m.email === email);
      const name =
        member?.name && member.name !== member.email ? member.name : undefined;
      return [...prev, { email, accessLevel: 'read', name }];
    });
  };

  const selectAccess = (email: string, accessLevel: PartnerAccessLevel) => {
    setSelectedPartners(prev => {
      const existing = prev.find(p => p.email === email);
      if (existing?.accessLevel === accessLevel) {
        return prev.filter(p => p.email !== email);
      }
      if (hasEmptyScope) {
        return prev;
      }
      const member = members.find(m => m.email === email);
      const name =
        member?.name && member.name !== member.email
          ? member.name
          : existing?.name;
      const next = prev.filter(p => p.email !== email);
      return [...next, { email, accessLevel, name }];
    });
  };

  const handleDone = () => {
    // Ensure all selected partners have names if available
    const partnersWithNames = selectedPartners.map(p => {
      if (!p.name) {
        const member = members.find(m => m.email === p.email);
        const name =
          member?.name && member.name !== member.email
            ? member.name
            : undefined;
        return { ...p, name };
      }
      return p;
    });
    onSelect(partnersWithNames);
    onClose();
  };

  const renderMemberItem = ({ item }: { item: TeamMember }) => {
    const selectedPartner = selectedPartners.find(p => p.email === item.email);
    const isSelected = !!selectedPartner;
    const hasName = item.name && item.name !== item.email;

    return (
      <View style={styles.memberItemContainer}>
        <View
          style={[styles.memberItem, isSelected && styles.selectedMemberItem]}
        >
          <TouchableOpacity
            style={styles.memberInfo}
            onPress={() => toggleSelection(item.email)}
            activeOpacity={0.7}
          >
            {hasName && (
              <Text
                style={[
                  styles.memberName,
                  isSelected && styles.selectedMemberName,
                ]}
              >
                {item.name}
              </Text>
            )}
            <Text
              style={[
                styles.memberEmail,
                isSelected && styles.selectedMemberEmail,
              ]}
            >
              {(item.email || '').toLowerCase()}
            </Text>
          </TouchableOpacity>
          <View style={styles.radioColumns}>
            <TouchableOpacity
              testID="partner-access-read"
              style={styles.radioHit}
              onPress={() => selectAccess(item.email, 'read')}
              accessibilityRole="radio"
              accessibilityState={{
                selected: selectedPartner?.accessLevel === 'read',
              }}
            >
              <View
                style={[
                  styles.radioOuter,
                  selectedPartner?.accessLevel === 'read' &&
                    styles.radioOuterSelected,
                ]}
              >
                {selectedPartner?.accessLevel === 'read' && (
                  <View style={styles.radioInner} />
                )}
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              testID="partner-access-readwrite"
              style={styles.radioHit}
              onPress={() => selectAccess(item.email, 'read_write')}
              accessibilityRole="radio"
              accessibilityState={{
                selected: selectedPartner?.accessLevel === 'read_write',
              }}
            >
              <View
                style={[
                  styles.radioOuter,
                  selectedPartner?.accessLevel === 'read_write' &&
                    styles.radioOuterSelected,
                ]}
              >
                {selectedPartner?.accessLevel === 'read_write' && (
                  <View style={styles.radioInner} />
                )}
              </View>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderHeader = () => (
    <>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>
          {title}{' '}
          {selectedPartners.length > 0 &&
            `(${selectedPartners.length} selected)`}
        </Text>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <X size={24} color="#333" />
        </TouchableOpacity>
      </View>

      {showScopeBanner && viewerDomain ? (
        <View style={styles.scopeBanner} accessibilityLiveRegion="polite">
          <Text style={styles.scopeBannerText}>
            Showing {viewerDomain} contributors
          </Text>
        </View>
      ) : null}

      <View style={styles.searchContainer}>
        <Search size={20} color="#999" style={styles.searchIcon} />
        <TextInput
          testID="partner-search"
          style={styles.searchInput}
          placeholder="Search by name or email"
          value={searchQuery}
          onChangeText={setSearchQuery}
          autoCorrect={false}
          autoCapitalize="none"
          returnKeyType="search"
          inputAccessoryViewID={
            Platform.OS === 'ios'
              ? PARTNER_CONTRIBUTOR_ACCESSORY_VIEW_ID
              : undefined
          }
        />
      </View>

      <View style={styles.columnHeaderRow}>
        <View style={styles.memberInfo} />
        <View style={styles.radioColumns}>
          <Text style={styles.columnHeader}>Read only</Text>
          <Text style={styles.columnHeader}>Read & Write</Text>
        </View>
      </View>
    </>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <KeyboardDismissToolbar
          nativeID={PARTNER_CONTRIBUTOR_ACCESSORY_VIEW_ID}
        />
        <View style={styles.modal} testID="partner-contributor-modal">
          {renderHeader()}

          <View style={styles.listContainer}>
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#ff6b35" />
                <Text style={styles.emptyText}>
                  Loading partner contributors...
                </Text>
              </View>
            ) : filteredMembers.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>
                  {isPersonalEmailViewer
                    ? 'A work email address is required to add partner contributors.'
                    : isMalformedEmailViewer
                    ? 'Your account email could not be used to find colleagues.'
                    : searchQuery
                    ? showScopeBanner && viewerDomain
                      ? `No colleagues match your search in ${viewerDomain}.`
                      : 'No matching partner contributors found'
                    : showScopeBanner && viewerDomain
                    ? `No partner contributors from ${viewerDomain} are available yet.`
                    : 'No partner contributors available'}
                </Text>
              </View>
            ) : (
              <FlatList
                data={filteredMembers}
                keyExtractor={(item, index) => `partner-${item.email}-${index}`}
                renderItem={renderMemberItem}
                contentContainerStyle={styles.listContent}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                removeClippedSubviews={true}
                maxToRenderPerBatch={10}
                windowSize={10}
              />
            )}
          </View>

          <View style={styles.footer}>
            <View style={styles.footerButtons}>
              <TouchableOpacity
                testID="partner-cancel-btn"
                style={styles.cancelButton}
                onPress={onClose}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="partner-done-btn"
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.doneButtonText}>
                  Done{' '}
                  {selectedPartners.length > 0 &&
                    `(${selectedPartners.length})`}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 600,
    ...Platform.select({
      web: {
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
      },
      default: {
        height: '80%',
      },
    }),
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        flexShrink: 0,
      },
    }),
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  closeButton: {
    padding: 4,
  },
  scopeBanner: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    ...Platform.select({
      web: {
        flexShrink: 0,
      },
    }),
  },
  scopeBannerText: {
    fontSize: 14,
    color: '#495057',
  },
  columnHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 4,
  },
  columnHeader: {
    width: 72,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
    ...Platform.select({
      web: {
        width: 88,
      },
    }),
  },
  radioColumns: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
  },
  radioHit: {
    width: 72,
    alignItems: 'center',
    paddingVertical: 8,
    minHeight: 44,
    justifyContent: 'center',
    ...Platform.select({
      web: {
        width: 88,
        minHeight: undefined,
        paddingVertical: 4,
      },
    }),
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#adb5bd',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: {
    borderColor: '#007bff',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#007bff',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    ...Platform.select({
      web: {
        flexShrink: 0,
      },
    }),
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    ...Platform.select({
      web: {
        outlineStyle: 'none' as const,
        outlineWidth: 0,
      },
    }),
  },
  listContainer: {
    flex: 1,
    ...Platform.select({
      web: {
        minHeight: 200,
        maxHeight: 'calc(90vh - 250px)',
        overflow: 'auto',
      },
      default: {
        // Same as TeamMemberSelector: flex: 1 fills the 80% sheet
      },
    }),
  },
  listContent: {
    paddingVertical: 8,
    paddingBottom: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  memberItemContainer: {
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  memberItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  selectedMemberItem: {
    backgroundColor: '#f0f7ff',
  },
  memberInfo: {
    flex: 1,
    marginRight: 12,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  selectedMemberName: {
    color: '#007bff',
  },
  memberEmail: {
    fontSize: 14,
    color: '#666',
  },
  selectedMemberEmail: {
    color: '#0056b3',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: '#ddd',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#007bff',
    borderColor: '#007bff',
  },
  footer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: spacing.modalInnerPadding,
    paddingVertical: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        flexShrink: 0,
      },
      default: {
        gap: spacing.md,
      },
    }),
  },
  footerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    ...Platform.select({
      web: {
        flexShrink: 0,
      },
      default: {
        alignSelf: 'flex-end',
      },
    }),
  },
  cancelButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  doneButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#ff6b35',
  },
  doneButtonText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
  },
  accessLevelContainer: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#f8f9fa',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  accessLevelLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  accessLevelButtons: {
    flexDirection: 'row',
  },
  accessLevelButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
    marginLeft: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  accessLevelButtonActive: {
    backgroundColor: '#007bff',
    borderColor: '#007bff',
  },
  accessLevelButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#666',
  },
  accessLevelButtonTextActive: {
    color: '#fff',
  },
});
